import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';

const STORAGE_KEY = 'pymed.fontScale';
const MIN_SCALE = 0.85;
const MAX_SCALE = 1.3;
const STEP = 0.05;
const BASE_PX = 16;

const FontScaleContext = createContext(null);

const clamp = (v) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, Math.round(v * 100) / 100));

export const FontScaleProvider = ({ children }) => {
  const [scale, setScale] = useState(() => {
    const stored = parseFloat(localStorage.getItem(STORAGE_KEY));
    return Number.isFinite(stored) ? clamp(stored) : 1;
  });

  useEffect(() => {
    document.documentElement.style.fontSize = `${BASE_PX * scale}px`;
    localStorage.setItem(STORAGE_KEY, String(scale));
  }, [scale]);

  const increase = useCallback(() => setScale((s) => clamp(s + STEP)), []);
  const decrease = useCallback(() => setScale((s) => clamp(s - STEP)), []);
  const reset = useCallback(() => setScale(1), []);

  const value = useMemo(
    () => ({
      scale,
      increase,
      decrease,
      reset,
      canIncrease: scale < MAX_SCALE,
      canDecrease: scale > MIN_SCALE,
      percent: Math.round(scale * 100),
      // 0–1 position within the allowed range, for the control's progress track.
      progress: (scale - MIN_SCALE) / (MAX_SCALE - MIN_SCALE),
    }),
    [scale, increase, decrease, reset]
  );

  return <FontScaleContext.Provider value={value}>{children}</FontScaleContext.Provider>;
};

export const useFontScale = () => {
  const ctx = useContext(FontScaleContext);
  if (!ctx) throw new Error('useFontScale must be used within a FontScaleProvider');
  return ctx;
};
